import { Grid, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";

import { useFormatter } from "../../../../utils/useFormatter";
import { FormButtonGroup } from "../../../../components/form";
import { useDict } from "../../../../utils/useDict";
import { useFhirData } from "../../../../utils/useFhirData";
import { useLocationContext } from "../../../../contexts/LocationContext";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useContractContext } from "@/contexts/ContractContext";
import { useAppointmentSubmission } from "./hooks/useAppointmentSubmission";
import { Steps } from "../../types";

interface IProps {
  scheduleActors: any;
  control: any;
  setValue: any;
  row: any;
  setModalOpen: any;
  handleSubmit: Function;
  onBackButtonClick: Function;
  handleNext: Function;
  setTabRefetch?: Dispatch<SetStateAction<boolean>>;
  walkin?: boolean;
}

export function ConfirmAppointment({
  control,
  row,
  scheduleActors,
  setValue,
  setModalOpen,
  onBackButtonClick,
  handleSubmit,
  setTabRefetch,
  walkin,
  handleNext,
}: IProps) {
  const { location: locationContext } = useLocationContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const { currentContract } = useContractContext();

  // Hook centralizado para submissão de agendamentos
  const { submitAppointment, loading } = useAppointmentSubmission({
    setValue,
    setModalOpen,
    setTabRefetch,
    walkin: walkin ?? false,
    control,
    onSuccess: () => {
      // Se for FOLLOWUP, atualiza o dashboard e fecha
      if (type === "FOLLOWUP") {
        if (setTabRefetch) setTabRefetch(true);
        setModalOpen(false);
      } else {
        handleNext(Steps.S7PaymentMethod);
      }
    },
  });

  let selectedPractitioner = useWatch({ name: "professionalActor", control });
  let healthcare = useWatch({ name: "healthCareServiceActor", control });
  let location = useWatch({ name: "locationActor", control });
  let patient = useWatch({ name: "patientActor", control });
  let type = useWatch({ name: "typeService", control });
  let time = useWatch({ name: "slotInfo", control });
  let timeStart = useWatch({ name: "timeStart", control });
  let timeEnd = useWatch({ name: "timeEnd", control });
  let serviceCostWatch = useWatch({ name: "serviceCost", control });
  let originalServiceCost = useWatch({ name: "originalServiceCost", control });

  const displayActors = [location, healthcare, selectedPractitioner, patient];

  const watchReferenceResponsible = useWatch({
    name: "auxObjReference",
    control,
  });

  const watchLocation = useWatch({ name: "locationActor", control });

  const auxVirtualService = useFhirData.virtualService(
    locationContext?.find((loc: any) =>
      loc?.resource?.id.includes(watchLocation?.reference?.split("/")?.[1])
    )?.resource
  );

  const appointmentIsFree = useWatch({ name: "appointmentIsFree", control });

  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan"
    );

  const onSubmit = async (data: any) => {
    if (hasFluxPayExtension) {
      handleNext();
      return;
    } else {
      // Usa o hook centralizado para submissão
      await submitAppointment({
        ...data,
        serviceCost: type === "FOLLOWUP" ? "" : data.serviceCost,
        appointmentIsFree: type === "FOLLOWUP" ? true : appointmentIsFree,
        timeStart,
        timeEnd,
        slotInfo: time,
      });
    }
  };
  const { formatNameDisplay } = useFormatter;

  useEffect(() => {
    if (row) {
      setValue("slotInfo", {
        id: row?.auxUpdateSlot.id,
        start: row?.slotTime.start,
        end: row?.slotTime.end,
      });
      setValue("auxUpdateSlot", row?.auxUpdateSlot);
    }
    if (scheduleActors) {
      setValue(
        "professionalActor",
        useFhirData.findActor(scheduleActors, "Practitioner/")
      );
      setValue(
        "healthCareServiceActor",
        useFhirData.findActor(scheduleActors, "HealthcareService")
      );

      setValue(
        "locationActor",
        useFhirData.findActor(scheduleActors, "Location")
      );
    }
    if (auxVirtualService !== watchLocation?.reference?.split("/")?.[1]) {
      setValue("isVirtual", auxVirtualService);
    }
    if (row?.serviceCost && !serviceCostWatch) {
      const initialCost = row?.serviceCost * 100;
      setValue(
        "serviceCost",
        useFormatter.formatCurrency(initialCost < 300 ? 300 : initialCost)
      );
    }
    if (row?.serviceCost && !originalServiceCost) {
      setValue(
        "originalServiceCost",
        useFormatter.formatCurrency(row?.serviceCost * 100)
      );
    }
  }, [
    row,
    setValue,
    scheduleActors,
    auxVirtualService,
    hasSubscriptionFluxPay,
  ]);

  return (
    <>
      <Stack
        width="100%"
        height="100%"
        flex="1"
        justifyContent="center"
        alignItems="center"
        spacing={4}
      >
        <Typography
          variant="h6"
          color="neutral700.main"
          textAlign="center"
          m={{ xs: 2, md: 5 }}
        >
          Você solicitou o agendamento de uma consulta com as seguintes
          características:
        </Typography>

        <Grid
          container
          justifyContent="space-between"
          m="16px 40px 36px 40px"
          rowSpacing={1}
          rowGap={4}
        >
          <Grid item xs={6}>
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Data e hora
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="subtitle2"
                  color="neutral700.main"
                  fontWeight={600}
                >
                  Dia: {dayjs(time?.start || timeStart).format("DD/MM/YYYY")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="subtitle2"
                  color="neutral700.main"
                  fontWeight={600}
                >
                  Hora:{" "}
                  {useFormatter.formatDateTimeToHour(time?.start || timeStart)}
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Tipo
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="subtitle2"
                  color="neutral700.main"
                  fontWeight={600}
                >
                  {useDict.appointmentType[type]}
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          {displayActors?.map((el: any, index: number) => {
            return (
              <Grid key={`${el}.${index}`} item xs={6}>
                <Stack direction="column" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {useDict.actor[el?.reference?.split("/")?.[0]] || "-"}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Typography
                      variant="subtitle2"
                      color="neutral700.main"
                      fontWeight={600}
                    >
                      {formatNameDisplay(el?.display)}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            );
          })}

          {watchReferenceResponsible?.ownerName && (
            <Grid key="Responsável" item xs={6}>
              <Stack direction="column" spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Responsável
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Typography
                    variant="subtitle2"
                    color="neutral700.main"
                    fontWeight={600}
                  >
                    {formatNameDisplay(watchReferenceResponsible?.ownerName)}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          )}
        </Grid>
        <FormButtonGroup
          disabled={!scheduleActors}
          nextButtonText={hasFluxPayExtension ? "Continuar" : "Finalizar"}
          goBackButtonText="Fechar"
          onGoBackButtonClick={() => {
            setModalOpen(false);
          }}
          onNextButtonClick={handleSubmit(onSubmit)}
          loading={loading}
        />
      </Stack>
    </>
  );
}
